import contextApi from "@/apis/ContextAPI";
import commonFn from "@/commons/commonFunction";
import store from "@/stores/store";
import { UserManager, WebStorageStateStore, User } from "oidc-client";
import { ModuleContext, ModuleOrganizationUnit, ModuleAuth } from "@/stores/module-const";

import router from "@/routers/router";
// import store from '@/stores/store';
import { MSEnum } from "@/commons/enumeration";
import httpClient from "@/apis/base/axios-httpclient";
import LoginHistoryAPI from "@/apis/LoginHistoryAPI";
import AuthAPI from "@/apis/AuthAPI";
import roleApi from "@/apis/UserSystem/UserAPI";
import { $toast, showAlert } from "@misa/ui-control";
import { useCache } from "@misa/ui-base";
import { CacheKeys } from "@/constants";
import { useAuthStore } from "@/stores/AuthStore";
import { RouteName } from "@/routers/routeNames";

class AuthService {
  /**
   * Xác thực token hiện tại
   * @param {Object} opts - { loginProvider, forceVerify }
   * UpdatedBy: dbhuan (11/06/2026) — Them loginProvider, forceVerify
   * UpdatedBy: dbhuan (12/06/2026) — Loai bo tham so idType khong dung
   */
  async verify({ loginProvider, forceVerify } = {}) {
    const response = await httpClient.getAsync({
      url: [window._apis.Auth, "/api/account/Verify"].join(""),
      params: { loginProvider, forceVerify },
    });
    if (response && useAuthStore().loginProvider === "MISAID_V2") {
      return { success: !!response.success };
    }
    if (response?.token) {
      store.commit(`${ModuleContext}/updateAccessTokenToState`, response.token);
      useAuthStore().setTokens({ accessToken: response.token });
    }
    return response;
  }

  // 	/**
  // 	 * Điều hướng sang màn đăng nhập
  // 	 * @param {*} userInfo
  // 	 */
  logoutExternal(userInfo) {
    window.location = location.origin + "/login";
  }

  // 	//Đăng xuất xóa phiên đăng nhập
  logOut() {
    //Xóa thông tin trong local storage
    let context = store.state[ModuleContext];
    if (context) {
      let user = context.User;
      //nếu chưa có thông tin token - kiểm tra localStorage
      // if (!user) {
      //   user = JSON.parse(commonFn.getLocalStorage("User"));
      // }
      let fn = [ModuleAuth, "logOut"].join("/");
      store.dispatch(fn, { UserName: user.username });
      //Điều hướng sang màn đăng nhập
      removeStorage();
      this.logoutExternal();
    }
  }

  async loginViaAmpl({ sid, tcode, env }) {
    try {
      const res = await AuthAPI.amplVerify({ sid, tcode, env });
      if (res && res.success && res.data) {
        const { accessToken, refreshToken } = res.data;
        useAuthStore().setTokens({ accessToken, refreshToken });
        store.commit(`${ModuleAuth}/updateAccessToken`, { accessToken, refreshToken });
        localStorage.setItem("loginProvider", "MC_AMIS");
        store.commit(`${ModuleContext}/updateToken`, res.data);
        store.commit(`${ModuleAuth}/updateAccessToken`, res.data);
        return { success: true };
      }
      return {
        success: false,
        errorCode: res?.errorCode,
        errorMessage: res?.errorMessage,
        data: res?.data,
      };
    } catch (err) {
      console.error("AMIS session verify error");
      return { success: false };
    }
  }

  /**
   * Lấy login provider hiện tại
   * UpdatedBy: dbhuan (09/06/2026)
   * @returns {string|null} "MISA_ID" | "MC_AMIS" | null
   */
  getLoginProvider() {
    return localStorage.getItem("loginProvider");
  }

  /**
   * Lấy AMIS Platform session ID từ localStorage
   * UpdatedBy: dbhuan (09/06/2026)
   * @returns {string|null}
   */
  getAmplSid() {
    return localStorage.getItem("_jp_AmplSid");
  }

  //Kiểm tra session MISA ID từ cookie — token không trả về client
  async isAuthenticated() {
    if (useAuthStore().hasToken) return true;

    try {
      const session = await AuthAPI.getMisaIdSession();
      if (!session?.success) {
        return false;
      }
      await useAuthStore().setMisaIdSession(session);
      return true;
    } catch {
      useAuthStore().clearMisaIdSession();
      return false;
    }
  }
  /**
   * Hàm lấy code_verifier từ oidc JetPayID
   */
  async getCodeVerifier() {
    const me = this;
    var userManager = new UserManager({ response_mode: "query" });
    var code_verifier = "";
    await userManager.readSigninResponseState().then(({ state, response }) => {
      if (state.request_type === "si:r") {
        code_verifier = state.code_verifier;
        return code_verifier;
      }
    });
    return code_verifier;
  }

  /**
   * Gọi API lấy config jetpayID
   */
  async _getConfig(idType = "JETPAYID") {
    const me = this;
    let fn = [ModuleContext, "getConfig"].join("/");
    return await store.dispatch(fn, { idType });
  }

  /**
   * Hàm lấy config
   * @param {string} idType - Loại ID xác thực (JETPAYID hoặc MISAID)
   */
  async getConfig(idType = "JETPAYID") {
    const me = this;
    let res = await me._getConfig(idType);
    return res;
  }

  /**
   * Hàm cập nhật token (VUEX)
   */
  async updateToken(payload) {
    useAuthStore().setTokens(payload);
    store.commit(`${ModuleAuth}/updateAccessToken`, payload);
    let fn = [ModuleContext, "updateToken"].join("/");
    return await store.dispatch(fn, payload);
  }

  /**
   * Gọi hàm lấy token trong VUEX(action)
   * @param {string} code - Authorization code từ OAuth flow
   * @param {string} idType - Loại ID xác thực (JETPAYID hoặc MISAID)
   */
  async _getToken(code, idType = "JETPAYID") {
    const me = this;
    let res = await me._getConfig(idType);
    var codeVerifier = JSON.parse(localStorage.getItem(res.client_id));
    let param = {
      code: code,
      code_verifier: codeVerifier.code_verifier,
      idType: idType,
    };
    let fn = [ModuleContext, "GetTokenJetPayId"].join("/");
    return await store.dispatch(fn, param);
  }

  /**
   * Hàm lấy token
   * @param {string} code - Authorization code từ OAuth flow
   * @param {string} idType - Loại ID xác thực (JETPAYID hoặc MISAID)
   */
  async getToken(code, idType = "JETPAYID") {
    let me = this;
    let res = await me._getToken(code, idType);
    return res;
  }

  /**
   * Lấy danh sách role của user từ API và cache bằng MISA Cache
   * Đồng thời cập nhật Vuex state + localStorage để tương thích ngược
   * UpdatedBy: dbhuan (11/06/2026) — Goi API truc tiep + MISA Cache
   */
  async getRoleLocal() {
    const { getCache } = useCache();
    const cache = getCache();
    try {
      const res = await roleApi.getListUserRole();
      const roleData = !!res && !res.error ? res : [];

      if (roleData.length === 0) {
        // let resLogout = await useAuthStore().logout({ router, skipRedirect: true });
        // DBHuan: Cập nhật thông báo sau khi đã thống nhất với nghiệp vụ
        await showAlert("Vui lòng liên hệ quản trị hệ thống để được phân quyền sử dụng.", {
          title: "Không có quyền truy cập ứng dụng",
          buttons: [
            {
              key: "close",
              text: "Đóng",
              type: "secondary",
              variant: "outline",
              onClick: () => "close",
            },
          ],
        });

        // if (resLogout?.loginUrl) {
        //   window.location.href = resLogout?.loginUrl;
        // } else {
        //   router.push({ name: RouteName.Login });
        // }

        return;
      }

      // Cache bằng MISA Cache
      await cache.set(CacheKeys.ROLE_LOCAL, undefined, roleData);
      // Cập nhật localStorage + Vuex state để tương thích ngược
      localStorage.setItem("RoleLocal", JSON.stringify(roleData));
      // store.commit("updateRoleLocal", roleData);
    } catch (e) {
      console.error("getRoleLocal error:", e);
    }
  }

  /**
   * Hàm cập nhật tài khoản đã kích hoạt VUEX(action)
   */
  async _updateUserActive(param) {
    let fn = [ModuleContext, "updateUserActive"].join("/");
    return await store.dispatch(fn, param);
  }

  /**
   * Hàm cập nhật tài khoản đã kích hoạt
   */
  async updateUserActive(id, scope) {
    const me = this;
    let param = {
      id: id,
      scope: scope,
    };
    let res = await me._updateUserActive(param);
    if (res.accessToken) {
      store.commit(`${ModuleAuth}/updateAccessToken`, res);
    }
    return res;
  }

  /**
   * Gọi hàm login trong VUEX(action)
   */
  async _login(param) {
    let fn = [ModuleContext, "login"].join("/");
    return await store.dispatch(fn, param);
  }
  /**
   * đăng nhập
   */
  async login(param) {
    let me = this;
    let res = await me._login(param);
    if (res.accessToken) {
      store.commit(`${ModuleAuth}/updateAccessToken`, res);
    }
    return res;
  }
  /**
   * Gọi hàm login trong VUEX(action)
   * @param {string} code - Authorization code từ OAuth flow
   * @param {string} idType - Loại ID xác thực (JETPAYID hoặc MISAID)
   */
  async _loginv2(code, idType = "JETPAYID") {
    const me = this;
    let res = await me._getConfig(idType);
    var codeVerifier = JSON.parse(localStorage.getItem(res.client_id));
    let param = {
      code: code,
      code_verifier: codeVerifier.code_verifier,
      returnUrl: res.redirect_uri,
      idType: idType,
    };
    let fn = [ModuleContext, "loginv2"].join("/");
    return await store.dispatch(fn, param);
  }

  /**
   * đăng ký
   */
  async register(param) {
    let me = this;
    me.trimParam(param);
    let fn = [ModuleContext, "register"].join("/");
    return await store.dispatch(fn, param);
  }

  /**
   * đăng nhập
   * @param {string} code - Authorization code từ OAuth flow
   * @param {string} idType - Loại ID xác thực (JETPAYID hoặc MISAID)
   */
  async loginV2(code, idType = "JETPAYID") {
    let me = this;
    let res = await me._loginv2(code, idType);
    if (res.accessToken) {
      store.commit(`${ModuleContext}/updateToken`, res);
      store.commit(`${ModuleAuth}/updateAccessToken`, res);
    }
    return res;
  }
  /**
   * xác nhận đăng ký
   */
  async collectConfirm(param) {
    let me = this;
    me.trimParam(param);
    let fn = [ModuleContext, "collectConfirm"].join("/");
    return await store.dispatch(fn, param);
  }

  /**
   * Gửi lại mã xác nhận
   */
  async resendOTP(param) {
    let me = this;
    me.trimParam(param);
    let fn = [ModuleContext, "resendOTP"].join("/");
    return await store.dispatch(fn, param);
  }

  /**
   * Gửi lại mã xác nhận
   */
  async resendOTPTwofactor(param) {
    let me = this;
    me.trimParam(param);
    let fn = [ModuleContext, "resendOTPTwofactor"].join("/");
    return await store.dispatch(fn, param);
  }

  /**
   * quên mật khẩu
   */
  async forgotPassword(param) {
    let me = this;
    me.trimParam(param);
    let fn = [ModuleAuth, "forgotPassword"].join("/");
    return await store.dispatch(fn, param);
  }

  /**
   * quên mật khẩu
   */
  async activeAccount(param) {
    let me = this;
    me.trimParam(param);
    let fn = [ModuleAuth, "activeAccount"].join("/");
    return await store.dispatch(fn, param);
  }

  /**
   * Xác nhận tài khoản
   */
  async confirmAccount(param) {
    let me = this;
    me.trimParam(param);
    let fn = [ModuleAuth, "confirmAccount"].join("/");
    return await store.dispatch(fn, param);
  }

  /**
   * xác nhận mật khẩu
   */
  async verifyPassword(param) {
    let me = this;
    me.trimParam(param);
    let fn = [ModuleAuth, "verifyPassword"].join("/");
    let res = await store.dispatch(fn, param);
    if (res.accessToken) {
      store.commit(`${ModuleContext}/updateToken`, res);
    }
    return res;
  }

  /**
   * reset mật khẩu
   */
  async resetPassword(param) {
    let me = this;
    me.trimParam(param);
    let fn = [ModuleAuth, "resetPassword"].join("/");
    return await store.dispatch(fn, param);
  }

  /**
   *  Xác thực 2 yếu tố
   */
  async verifyTwf(param) {
    let me = this;
    me.trimParam(param);
    let fn = [ModuleAuth, "verifyTwf"].join("/");
    let res = await store.dispatch(fn, param);
    if (res.accessToken) {
      store.commit(`${ModuleContext}/updateToken`, res);
      setTimeout(() => {
        router.push("/");
      }, 500);
    }
    return res;
  }

  /**
   * thử cách khác (xác thực 2 bước)
   */
  async anotherWay(param) {
    let me = this;
    me.trimParam(param);
    let fn = [ModuleAuth, "anotherWay"].join("/");
    return await store.dispatch(fn, param);
  }

  /**
   * Đếm ngược
   */
  startTimer(duration, display, hide) {
    var timer = duration,
      minutes,
      seconds;
    var inter = setInterval(function () {
      minutes = parseInt(timer / 60, 10);
      seconds = parseInt(timer % 60, 10);

      minutes = minutes < 10 ? "0" + minutes : minutes;
      seconds = seconds < 10 ? "0" + seconds : seconds;

      display.querySelector("#time").textContent = minutes + ":" + seconds;

      if (--timer < 0) {
        clearInterval(inter);
        display.style.display = "none";
        hide.style.display = "block";
      }
    }, 1000);
  }

  /**
   * Bỏ khoảng trắng hai đầu dữ liệu
   * @param {Tham số truyền vào} object
   */
  trimParam(object) {
    for (const property in object) {
      let myVar = object[property];
      if (typeof myVar === "string" || myVar instanceof String) {
        object[property] = myVar.trim();
      }
    }
  }

  /**
   * Kiểm tra đã getting chưa
   */
  isGetting() {
    let getting = window._getting;
    if (!getting) {
      getting = commonFn.getLocalStorage("GettingStarted");
      if (getting) {
        getting = JSON.parse(getting);
      }
    }
    return getting.GettingStarted;
  }

  /**
   * Ham check xác thực
   */
  async checkAccountVerification(userName) {
    const me = this;
    let param = {
      userName: userName,
    };
    let fn = [ModuleContext, "checkAccountVerification"].join("/");
    return await store.dispatch(fn, param);
  }

  /**
   * Kiểm tra tồn tại tài khoản MISA ID
   * @param {string} username - Email của người dùng cần kiểm tra
   * @returns {Promise<object>} - Kết quả kiểm tra { exists, message, errorCode }
   */
  async checkMisaIdExists(username) {
    try {
      const url = `${window._apis.Auth}/api/account/misaid/check-exists`;
      const response = await httpClient.postAsync({
        url: url,
        data: {
          username: username,
        },
      });
      return response;
    } catch (error) {
      console.error("Error checking MISA ID existence:", error);
      return {
        exists: false,
        message: "Lỗi khi kiểm tra tài khoản MISA ID",
        errorCode: 999,
      };
    }
  }

  /**
   * Gọi API chuyển đổi từ JetPay ID sang MISA ID
   * @param {string} username - Email của người dùng cần chuyển đổi
   * @returns {Promise<object>} - Kết quả chuyển đổi { exists, message, errorCode }
   */
  async convertJpidToMisaId(username) {
    try {
      const url = `${window._apis.Auth}/api/account/convert-jpid-to-misaid`;
      const response = await httpClient.postAsync({
        url: url,
        data: {
          username: username,
        },
        headers: {
          accept: "text/plain",
          "X-Requested-With": "XMLHttpRequest",
        },
      });
      return response;
    } catch (error) {
      console.error("Error converting JPID to MISAID:", error);
      return {
        exists: false,
        message: "Có lỗi xảy ra. Vui lòng thử lại!",
        errorCode: 999,
      };
    }
  }

  /**
   * Kiểm tra tài khoản đã bật xác thực hai lớp chưa
   * @returns {Promise<boolean>} - true nếu đã bật 2FA, false nếu chưa bật
   */
  async GetMisaIdUser() {
    try {
      const url = `${window._apis.Auth}/api/account/misa-id-user`;
      const response = await httpClient.postAsync({
        url: url,
      });
      return response;
    } catch (error) {
      console.error("Error checking 2FA status:", error);
      return {};
    }
  }

  /**
   * Ghi lại lịch sử đăng nhập
   */
  async recordLoginHistory() {
    try {
      return await LoginHistoryAPI.recordLoginHistory();
    } catch (error) {
      console.error("Error recording login history:", error);
      return null;
    }
  }
}

export default new AuthService();
